import React, { useState } from 'react';
import { View, KeyboardAvoidingView, Platform, Image, Text, ScrollView } from 'react-native';
import { Button } from 'react-native-paper';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { login } from '../redux/slices/authSlice';
import { RADIUS, shadow, TYPOGRAPHY } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { useLayout } from '../theme/useLayout';
import { TextInput, ThemeToggle } from '../components/ui';

export default function LoginScreen() {
  const dispatch = useAppDispatch();
  const styles = useStyles();
  const { colors } = useAppTheme();
  const { isDesktop } = useLayout();
  const insets = useSafeAreaInsets();
  const { loading, error } = useAppSelector((s) => s.auth);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = () => {
    if (username.trim() && password.trim()) {
      dispatch(login({ username: username.trim(), password: password.trim() }));
    }
  };

  return (
    <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <View style={[styles.toggle, { top: insets.top + 12 }]}>
        <ThemeToggle />
      </View>
      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
        <View style={[styles.card, isDesktop && styles.cardDesktop, isDesktop && shadow(colors, 3)]}>
          <View style={styles.cardHeader}>
            <Text style={styles.cardHeaderText}>Connexion</Text>
          </View>

          <View style={styles.body}>
            <View style={styles.brandRow}>
              <Image source={require('../../assets/logo.jpeg')} style={styles.logo} resizeMode="cover" />
              <View>
                <Text style={styles.brandName}>
                  geo<Text style={styles.brandAccent}>fleete</Text>
                </Text>
                <Text style={styles.tagline}>Localisez · Sécurisez · Simplifiez</Text>
              </View>
            </View>

            <Text style={styles.title}>Bienvenue sur votre espace de gestion</Text>

            <TextInput
              label="Nom d'utilisateur"
              value={username}
              onChangeText={setUsername}
              style={styles.input}
              autoCapitalize="none"
              autoComplete="username"
              returnKeyType="next"
              left={<TextInput.Icon icon="account-outline" />}
            />

            <TextInput
              label="Mot de passe"
              value={password}
              onChangeText={setPassword}
              secureTextEntry={!showPassword}
              style={styles.input}
              autoComplete="password"
              returnKeyType="go"
              onSubmitEditing={handleLogin}
              left={<TextInput.Icon icon="lock-outline" />}
              right={
                <TextInput.Icon
                  icon={showPassword ? 'eye-off-outline' : 'eye-outline'}
                  onPress={() => setShowPassword((v) => !v)}
                  accessibilityLabel={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                />
              }
            />

            {error ? (
              <View style={styles.errorBox} accessibilityRole="alert">
                <MaterialCommunityIcons name="alert-circle-outline" size={18} color={colors.danger} />
                <Text style={styles.errorText}>{error}</Text>
              </View>
            ) : null}

            <Button
              mode="contained"
              onPress={handleLogin}
              loading={loading}
              disabled={loading || !username || !password}
              style={styles.button}
              contentStyle={styles.buttonContent}
              labelStyle={styles.buttonLabel}
            >
              Continuer
            </Button>
          </View>
        </View>
        <Text style={styles.footer}>© {new Date().getFullYear()} GeoFleete — Gestion GPS</Text>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const useStyles = makeStyles((c) => ({
  container: { flex: 1, backgroundColor: c.mode === 'dark' ? c.background : c.surfaceAlt },
  toggle: { position: 'absolute', right: 16, zIndex: 2 },
  scroll: { flexGrow: 1, alignItems: 'center', justifyContent: 'center', padding: 16, paddingVertical: 48 },
  card: {
    width: '100%',
    maxWidth: 480,
    backgroundColor: c.surface,
    borderRadius: RADIUS.xl,
    borderWidth: 1,
    borderColor: c.divider,
    overflow: 'hidden',
  },
  cardDesktop: { maxWidth: 520 },
  cardHeader: {
    alignItems: 'center',
    paddingVertical: 18,
    borderBottomWidth: 1,
    borderBottomColor: c.divider,
  },
  cardHeaderText: { fontSize: 16, fontWeight: '800', color: c.textPrimary },
  body: { padding: 24, paddingTop: 28 },
  brandRow: { flexDirection: 'row', alignItems: 'center', gap: 14, marginBottom: 24 },
  logo: { width: 52, height: 52, borderRadius: RADIUS.md },
  brandName: { fontSize: 26, fontWeight: '800', color: c.brand, letterSpacing: -0.8 },
  brandAccent: { color: c.primary },
  tagline: { fontSize: 13, color: c.textSecondary, marginTop: 2 },
  title: { ...TYPOGRAPHY.title, color: c.textPrimary, marginBottom: 20 },
  input: { marginBottom: 14 },
  errorBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: c.dangerSoft,
    borderRadius: RADIUS.md,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 14,
  },
  errorText: { flex: 1, fontSize: 14, color: c.danger, fontWeight: '500' },
  button: { marginTop: 6, borderRadius: RADIUS.sm },
  buttonContent: { height: 50 },
  buttonLabel: { fontSize: 16, fontWeight: '700' },
  footer: { fontSize: 12, color: c.textMuted, marginTop: 24 },
}));
